import { useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  DollarSign,
  Upload,
  FileText,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Textarea } from '@/app/components/ui/textarea';
import {
  useExpense,
  useSubmitExpense,
  useApproveExpense,
  useRejectExpense,
  useReimburseExpense,
  useUploadReceipt,
} from '@/hooks/use-payroll';
import { usePermissions } from '@/hooks/use-permissions';
import { ApprovalError, SelfApprovedBadge, useSelfApproval } from '@/app/components/payroll/self-approval';
import { useOrgCurrency, useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import { resolveMediaUrl } from '@/lib/api-client';
import { formatMoney } from '@/lib/currency';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  reimbursed: 'bg-purple-100 text-purple-800',
};

export function ExpenseDetailPage() {
  const timeZone = useOrgTimeZone();
  const orgCurrency = useOrgCurrency();
  const { id } = useParams<{ id: string }>();
  const permissions = usePermissions();
  const isManager = permissions.canManagePayroll;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const expenseId = Number(id);
  const { data: expense, isLoading } = useExpense(expenseId);

  const submitExpense = useSubmitExpense();
  const approveExpense = useApproveExpense();
  const rejectExpense = useRejectExpense();
  const reimburseExpense = useReimburseExpense();
  const uploadReceipt = useUploadReceipt();

  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const selfApproval = useSelfApproval('expense');

  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-500">Loading expense...</div>
    );
  }

  if (!expense) {
    return (
      <div className="p-8 text-center text-gray-500">Expense not found</div>
    );
  }

  const handleSubmit = async () => {
    await submitExpense.mutateAsync(expenseId);
  };

  const handleApprove = () =>
    selfApproval.approve(expenseId, (confirmSelfApproval) =>
      approveExpense.mutateAsync({ id: expenseId, confirmSelfApproval }),
    );

  const handleReject = async () => {
    if (!rejectionReason.trim()) return;
    await rejectExpense.mutateAsync({ id: expenseId, reason: rejectionReason });
    setShowRejectForm(false);
    setRejectionReason('');
  };

  const handleReimburse = async () => {
    await reimburseExpense.mutateAsync(expenseId);
  };

  const handleUploadReceipt = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadReceipt.mutateAsync({ expenseId, file });
    e.target.value = '';
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link to="/payroll/expenses">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{expense.description}</h1>
          <p className="text-gray-600">{expense.person_name ?? expense.tech_name ?? 'Unknown'} &middot; {expense.date}</p>
        </div>
        <Badge className={`text-sm ${statusColors[expense.status] ?? ''}`}>
          {expense.status.replace('_', ' ')}
        </Badge>
        <SelfApprovedBadge selfApproved={expense.self_approved} />
      </div>

      <ApprovalError message={selfApproval.errorFor(expenseId)} />
      {selfApproval.dialog}

      {/* Action Buttons */}
      <div className="flex gap-2 flex-wrap">
        {expense.status === 'draft' && (
          <>
            <Button onClick={handleSubmit} disabled={submitExpense.isPending}>
              Submit for Approval
            </Button>
            <Button variant="outline" asChild>
              <Link to={`/payroll/expenses/${expense.id}`}>Edit</Link>
            </Button>
          </>
        )}
        {isManager && (expense.status === 'submitted' || expense.status === 'under_review') && (
          <>
            <Button onClick={handleApprove} disabled={approveExpense.isPending}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve
            </Button>
            <Button
              variant="destructive"
              onClick={() => setShowRejectForm(true)}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject
            </Button>
          </>
        )}
        {isManager && expense.status === 'approved' && (
          <Button onClick={handleReimburse} disabled={reimburseExpense.isPending}>
            <DollarSign className="h-4 w-4 mr-2" />
            Mark Reimbursed
          </Button>
        )}
      </div>

      {/* Rejection Form */}
      {showRejectForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-red-600">Reject Expense</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              placeholder="Reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
            />
            <div className="flex gap-2">
              <Button variant="destructive" onClick={handleReject} disabled={rejectExpense.isPending}>
                Confirm Rejection
              </Button>
              <Button variant="outline" onClick={() => setShowRejectForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rejection Reason */}
      {expense.status === 'rejected' && expense.rejection_reason && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
            <p className="text-sm text-red-700 mt-1">{expense.rejection_reason}</p>
            {expense.reviewed_by_username && (
              <p className="text-xs text-red-500 mt-2">
                Rejected by {expense.reviewed_by_username} on{' '}
                {expense.reviewed_at
                  ? formatDateTimeInTimeZone(expense.reviewed_at, timeZone, { dateStyle: 'medium' })
                  : ''}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expense Info */}
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Amount</dt>
                <dd className="text-sm font-medium">
                  {formatMoney(expense.amount, expense.currency ?? orgCurrency)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Category</dt>
                <dd className="text-sm font-medium">{expense.category_name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Vendor</dt>
                <dd className="text-sm font-medium">{expense.vendor || '-'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Payment Method</dt>
                <dd className="text-sm font-medium">{expense.payment_method.replace('_', ' ')}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Billable</dt>
                <dd className="text-sm font-medium">{expense.is_billable ? 'Yes' : 'No'}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Reimbursable</dt>
                <dd className="text-sm font-medium">{expense.is_reimbursable ? 'Yes' : 'No'}</dd>
              </div>
              {expense.notes && (
                <div>
                  <dt className="text-sm text-gray-500 mb-1">Notes</dt>
                  <dd className="text-sm">{expense.notes}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        {/* Receipts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Receipts
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Upload
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              className="hidden"
              onChange={handleUploadReceipt}
            />
            {expense.receipts.length === 0 ? (
              <p className="text-sm text-gray-500">No receipts attached</p>
            ) : (
              <div className="space-y-2">
                {expense.receipts.map((receipt) => (
                  <div
                    key={receipt.id}
                    className="flex items-center gap-3 p-2 bg-gray-50 rounded"
                  >
                    <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{receipt.file_name}</p>
                      <p className="text-xs text-gray-500">
                        {formatDateTimeInTimeZone(receipt.uploaded_at, timeZone, { dateStyle: 'medium' })}
                      </p>
                    </div>
                    <a
                      href={resolveMediaUrl(receipt.file)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex-shrink-0"
                    >
                      View
                    </a>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Allocations */}
      {expense.allocations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Project Allocations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {expense.allocations.map((alloc) => (
                <div
                  key={alloc.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded"
                >
                  <div>
                    <p className="font-medium">{alloc.project_name}</p>
                    {alloc.cost_code && (
                      <p className="text-xs text-gray-500">Code: {alloc.cost_code}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">
                      {formatMoney(alloc.amount, expense.currency ?? orgCurrency)}
                    </p>
                    {alloc.percentage && (
                      <p className="text-xs text-gray-500">{alloc.percentage}%</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
